import { memo, useEffect, useMemo, useRef, useState } from 'react';
import { fetchSharedAudioSrcEffect } from 'app/store/Shares/effects';
import { Navigate, Outlet, useLocation } from 'react-router-dom';
import { usePlaylistFeatureFlag } from 'featureFlags';
import { useIsMobile, useSyncIntercom } from 'app/utils/hooks';
import { Box, Flex } from '@chakra-ui/react';
import { useSelector } from 'react-redux';
import { getSharedPlaylist } from 'app/store/Shares/selectors';
import { PlaylistDetails } from 'app/store/AudioPlayer/slice';
import { useAudioPlayer } from 'app/components/AudioPlayer/hooks';
import { AudioPlayer } from 'app/components/AudioPlayer';
import { useAppSelector } from 'app/store';
import { getAuthenticatedSelector } from 'app/store/Auth/selectors';
import { SharedAudioHeaderBar } from 'app/components/Playlist/SharedAudioHeaderBar';
import { SharedPlaylist, SharedPlaylistResponse } from 'types/shares.types';
import {
  AudioShare,
  ShareResponse,
  ShareType,
} from 'app/components/Shares/types';
import { useDownloadSharedAudio } from 'app/components/Shares/AudiosShare/hooks';
import { getSharedAudio } from 'app/components/Shares/AudiosShare/utils';
import { PlaylistDescriptionDialog } from 'app/components/Playlist/PlaylistDescriptionDialog';
import { SharedPlaylistHeaderLoader } from 'app/components/Playlist/SharedPlaylistHeaderLoader';
import { ChakraEmptyList, ContentContainer } from 'app/components/Core';
import { ContentContainerType } from 'types/enums';
import { ReadOnlyPlaylist } from 'app/components/Playlist/ReadOnlyPlaylist';
import { AudioShareHeader } from './AudioShareHeader';

interface AudiosShareComponentProps {
  sharedPlaylist: SharedPlaylist | undefined;
  sharedAudio?: AudioShare;
  playlistDetails: PlaylistDetails | undefined;
  isAuthenticated: boolean;
  shareType?: ShareType;
  onDownload?: () => void;
  song?: ShareResponse['response']['song']['item'];
}

const AudiosShareComponent = ({
  sharedPlaylist,
  sharedAudio,
  playlistDetails,
  isAuthenticated,
  shareType,
  onDownload,
  song,
}: AudiosShareComponentProps) => {
  const isMobile = useIsMobile();
  const [descriptionDialogOpen, setDescriptionDialogOpen] = useState(false);
  const scrollableContainerRef = useRef<HTMLDivElement>(null);
  const { loadAudio, stop } = useAudioPlayer();

  const { pathname } = useLocation();
  const showLyrics = pathname.includes('lyrics');

  useEffect(() => {
    if (sharedPlaylist) {
      loadAudio({
        audio: sharedPlaylist.queue,
        playMode: 'playlist',
        playlistDetails,
        playOnInitialLoad: false,
      });
    }
    return () => {
      stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sharedPlaylist, playlistDetails]);

  const haveAudios = sharedPlaylist?.queue.length || sharedAudio;

  return (
    <>
      <Flex
        direction="column"
        justifyContent="space-between"
        height="var(--chakra-vh)"
        minHeight="var(--chakra-vh)"
      >
        <Box>
          <SharedAudioHeaderBar
            referralId={
              isAuthenticated ? undefined : sharedPlaylist?.referralId
            }
            sx={{
              position: 'initial',
            }}
          />
        </Box>
        <Flex
          flex={1}
          overflowY="auto"
          overflowX="hidden"
          position="relative"
          direction="column"
          ref={scrollableContainerRef}
        >
          <Outlet />
          <PlaylistDescriptionDialog
            open={descriptionDialogOpen}
            onClose={() => setDescriptionDialogOpen(false)}
            description={playlistDetails?.description}
            title={playlistDetails?.title || ''}
          />
          <Box
            h={showLyrics ? 0 : 'auto'}
            overflow={showLyrics ? 'hidden' : 'auto'}
          >
            {sharedPlaylist ? (
              <AudioShareHeader
                sharedPlaylist={sharedPlaylist}
                shareType={shareType}
                song={song}
              />
            ) : (
              <SharedPlaylistHeaderLoader />
            )}
            <ContentContainer
              type={ContentContainerType.LIST}
              isViewContent
              paddingTop={isMobile ? 0 : 6}
              paddingBottom={0}
              maxHeight="initial"
            >
              {haveAudios ? (
                <ReadOnlyPlaylist
                  playlist={sharedPlaylist}
                  shareType={shareType}
                  onDownload={onDownload}
                  playlistDetails={playlistDetails}
                  openDescriptionDialog={() => setDescriptionDialogOpen(true)}
                />
              ) : (
                <ChakraEmptyList
                  title=""
                  primaryText="This playlist dosen't contain any audio"
                />
              )}
            </ContentContainer>
          </Box>
        </Flex>
        {haveAudios && (
          <Box>
            <AudioPlayer
              transitionType="persistent"
              isPlayerExpandable={false}
              customFetchAudioForPlayingEffect={fetchSharedAudioSrcEffect}
              isWaveformFeatureEnabled={Boolean(
                sharedPlaylist?._links.getDesktopWaveform ||
                  sharedAudio?._links.getDesktopWaveform
              )}
              songCover={shareType === ShareType.AUDIO}
              images={sharedPlaylist?.images}
            />
          </Box>
        )}
      </Flex>
    </>
  );
};

const AudiosShareComponentMemoized = memo(
  AudiosShareComponent,
  (prev, next) => {
    return (
      prev.sharedPlaylist?.guid === next.sharedPlaylist?.guid &&
      prev.playlistDetails?.guid === next.playlistDetails?.guid
    );
  }
);

interface AudiosShareProps {
  shareResponse: ShareResponse | SharedPlaylistResponse;
}

export const AudiosShare = ({ shareResponse }: AudiosShareProps) => {
  useSyncIntercom(false);
  const { playlistFeatureEnabled } = usePlaylistFeatureFlag();
  const isAuthenticated = useAppSelector(getAuthenticatedSelector);
  const sharedPlaylist = useSelector(getSharedPlaylist);
  const sharedAudio = getSharedAudio(shareResponse);
  const song = (shareResponse as ShareResponse).response?.song?.item;
  const { onDownloadAudio } = useDownloadSharedAudio({ audio: sharedAudio });

  const playlistDetails: PlaylistDetails | undefined = useMemo(() => {
    if (!sharedPlaylist) {
      return undefined;
    }
    return {
      guid: sharedPlaylist.guid,
      title: sharedPlaylist.title,
      description: sharedPlaylist.description,
      author: sharedPlaylist.author,
    };
  }, [sharedPlaylist]);

  if (!playlistFeatureEnabled) {
    return <Navigate to="/" />;
  }

  return (
    <AudiosShareComponentMemoized
      isAuthenticated={isAuthenticated}
      sharedPlaylist={sharedPlaylist}
      sharedAudio={sharedAudio}
      playlistDetails={playlistDetails}
      shareType={shareResponse.response.share.item.type}
      onDownload={onDownloadAudio}
      song={song}
    />
  );
};
